// Types

/**
 * Enum representing ratings A, B, C, D, and E.
 */
export enum Rating {
  /**
   * A Rating
   */
  A = 'A',
  /**
   * B Rating
   */
  B = 'B',
  /**
   * C Rating
   */
  C = 'C',
  /**
   * D Rating
   */
  D = 'D',
  /**
   * E Rating
   */
  E = 'E',
}

/**
 * An array of available ratings.
 */
export const RatingList = Object.keys(Rating) as Rating[];

/**
 * Represents the different routes of the application.
 */
export enum AppPath {
  /**
   * The path to home page.
   */
  HOME = '/',
  /**
   * The path to fleet CII ratings page.
   */
  FLEET_CII_RATINGS = '/fleet-cii-ratings',
  /**
   * The path to CII simulator.
   */
  CII_SIMULATOR = '/cii-simulator',
  /**
   * The path to fleet EEOI scores page.
   */
  FLEET_EEOI_SCORES = '/fleet-eeoi-scores',
  /**
   * The path to fleet performance analysis page.
   */
  FLEET_PERFORMANCE_ANALYSIS = '/fleet-performance',
  /**
   * The path to auth.
   */
  AUTH = '/auth',
  /**
   * The path to login page.
   *
   * Due to a bug in the login form right after logout, it is important to avoid using the next router. Use the normal <a> or location.href
   */
  LOGIN = '/auth/login',
  /**
   * The path to expired link page.
   */
  EXPIRED_LINK = '/auth/expired-link',
  /**
   * The path to change password page.
   */
  CHANGE_PASSWORD = '/auth/change-password',
  /**
   * The path to forgot password page.
   */
  FORGOT_PASSWORD = '/auth/forgot-password',
  /**
   * The path to server error.
   */
  SERVER_ERROR = '/500',
  /**
   * The path to not found page.
   */
  NOT_FOUND = '/404',
  /**
   * The path to account created successfully page.
   */
  ACCOUNT_CREATED_SUCCESSFULLY = '/account-created-successfully',
}

/**
 * Represents the different routes of the Liners application.
 */
export enum LinersAppPaths {
  DeploymentPlanning = '/deployment-planning',
  DeploymentPlanningSchedules = '/deployment-planning/deployment-schedules',
  ScheduleRecovery = '/schedule-delays',
  ScheduleRecoveryNewDelay = '/schedule-delays/new-delay',
  CargoPrediction = '/cargo-prediction',
  VesselModels = '/vessel-models',
  VesselProfiler = '/vessel-profiler',
  PerformanceSimulator = '/performance-simulator',
  FoulingSimulator = '/fouling-simulator',
  AiChatbot = '/ai-chatbot',
}

/**
 * Represents the different API paths of the Liners backend.
 */
export enum LinersApiPaths {
  Login = '/auth/login',
  Logout = '/auth/logout',
  VerifyToken = '/auth/verify_token',
  UserDetails = '/user?user_details=true&client_details=true',
  ConfirmSignUp = '/auth/confirm_sign_up',
  RequestForgotPassword = '/auth/forgot_password/request',
  ConfirmForgotPassword = '/auth/forgot_password/confirm',
}

/**
 * Represents the different homepages assignable to the user.
 */
export enum UserHomePaths {
  Cii = AppPath.HOME,
  CargoPrediction = LinersAppPaths.CargoPrediction,
  ScheduleRecovery = LinersAppPaths.ScheduleRecovery,
  VesselProfiler = LinersAppPaths.VesselProfiler,
}

/**
 * Map of home page attributes to the corresponding home page paths.
 */
export const homePathByByAttributeMap = new Map([
  ['home_page:cii_home', UserHomePaths.Cii],
  ['home_page:cargo_prediction', UserHomePaths.CargoPrediction],
  ['home_page:schedule_recovery', UserHomePaths.ScheduleRecovery],
  ['home_page:vessel_profiler', UserHomePaths.VesselProfiler],
]);

/**
 * This enum can be used to indicate the sorting direction when implementing sorting functionality in a component or application.
 */
export enum SortOptions {
  /**
   * Sort option for ascending order.
   */
  ASCENDANT = 'asc',

  /**
   * Sort option for descending order.
   */
  DESCENDANT = 'desc',
}

/**
 * The default value for limiting the vessels.
 */
export const DEFAULT_VESSEL_LIMIT = 20;

/**
 * The wait time for resetting the password in seconds.
 */
export const resetPasswordWaitTime = 60;

/**
 * Enum representing different customers
 */
export enum Customers {
  KLINE = 'KLINE',
}

/**
 * Name o the broadcast channel for logout.
 */
export const LOGOUT_CHANNEL = 'logout-channel';

/**
 * The logout detail message from the API endpoint
 */
export const LOGOUT_DETAIL = 'User logged out successfully.';

/**
 * The next-auth provider used for authentication.
 */
export const AUTH_PROVIDER = 'credentials';

/**
 * The name of the query param used for share the access token.
 */
export const ACCESS_TOKEN = 'data';

/**
 * The name of the attribute used to identify the users home page.
 */
export const HOME_PAGE_ATTRIBUTE = 'home_page';

/**
 * An array of public routes that do not require authentication.
 */
export const PUBLIC_ROUTES = [
  // Public routes that do not require authentication
  AppPath.LOGIN,
  AppPath.CHANGE_PASSWORD,
  AppPath.FORGOT_PASSWORD,
  AppPath.EXPIRED_LINK,
];
